import { Bot, Check, GitPullRequest, KeyRound, Search, ShieldCheck } from "lucide-react";

import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PersonalMcpKeys } from "./PersonalMcpKeys";

function Command({ children }: { children: string }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-sm border bg-muted/60 p-3 text-xs leading-relaxed">
      <code>{children}</code>
    </pre>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: string }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary font-mono text-xs font-bold text-primary-foreground">
        {number}
      </span>
      <span>
        <span className="block font-semibold">{title}</span>
        <span className="mt-0.5 block text-sm text-muted-foreground">{children}</span>
      </span>
    </li>
  );
}

function AccessBoundary() {
  const steps = [
    [
      "Employee identity",
      "Google sign-in resolves the managed DAS employee. Disabled employees fail closed.",
    ],
    [
      "One customer connection",
      "During authorization, choose one assigned customer. The grant cannot cross into another customer.",
    ],
    [
      "Allowed capabilities",
      "The grant keeps only the requested OAuth scopes that current employee permissions allow.",
    ],
    [
      "Exact knowledge scope",
      "Every search, listing, or file read names company-wide knowledge or one deployed tool.",
    ],
    [
      "Permission recheck",
      "The server checks current employee and customer permissions again on every action.",
    ],
    [
      "Audit trail",
      "Reads, searches, uploads, and context changes are attributed to the employee.",
    ],
  ] as const;
  return (
    <Card className="mt-5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-5" /> How the boundary works
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="grid gap-5 md:grid-cols-2">
          {steps.map(([title, description], index) => (
            <Step key={title} number={index + 1} title={title}>
              {description}
            </Step>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function ClientConnections({ endpoint }: { endpoint: string }) {
  return (
    <div className="mt-5 grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="size-5" /> Connect Codex
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-muted-foreground">
            Add the remote service, then authorize read and search access in your browser.
          </p>
          <Command>{`codex mcp add das-operations --url ${endpoint} --oauth-resource ${endpoint}`}</Command>
          <Command>{`codex mcp login das-operations --scopes ops:read,ops:search`}</Command>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="size-5" /> Connect Claude Code
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-muted-foreground">
            Add the same HTTP MCP service and complete the browser authorization.
          </p>
          <Command>{`claude mcp add --transport http --scope user das-operations ${endpoint}`}</Command>
          <Command>claude mcp login das-operations</Command>
        </CardContent>
      </Card>
    </div>
  );
}

function OAuthScopes() {
  const scopes = [
    [
      "ops:read",
      "Customer metadata, scoped notes, artifact lists, and paged text from supported files.",
    ],
    ["ops:search", "Semantic search over company-wide knowledge or one exact tool."],
    [
      "ops:write",
      "Upload artifacts and add structured context when the employee also has write access.",
    ],
  ] as const;
  return (
    <Card className="mt-5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-5" /> What the scopes permit
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        {scopes.map(([scope, description]) => (
          <div className="rounded-sm border p-4" key={scope}>
            <p className="font-mono text-xs font-bold">{scope}</p>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function KnowledgeScope() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="size-5" /> Company versus tool scope
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <p className="font-semibold">Company-wide knowledge</p>
          <Command>{`{ "kind": "customer" }`}</Command>
          <p className="mt-2 text-muted-foreground">
            Includes only artifacts and notes not linked to a tool.
          </p>
        </div>
        <div>
          <p className="font-semibold">One tool</p>
          <Command>{`{ "kind": "tool", "toolId": "tool_…" }`}</Command>
          <p className="mt-2 text-muted-foreground">
            Includes only knowledge linked to that actively deployed customer tool.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function DeploymentBoundary() {
  const controls = [
    "Engineers and local AI edit the correct tool repository on a branch.",
    "Review and merge to main use the normal GitHub permissions and controls.",
    "CI holds deployment credentials and records the release provenance.",
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitPullRequest className="size-5" /> Deployments stay separate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">
        <p>
          MCP supplies customer context. It does not expose production deploy credentials or a
          deploy action.
        </p>
        {controls.map((control) => (
          <p className="flex gap-2" key={control}>
            <Check className="mt-0.5 size-4 shrink-0 text-primary" /> {control}
          </p>
        ))}
      </CardContent>
    </Card>
  );
}

export function AiAccessView() {
  const endpoint = `${window.location.origin}/mcp`;
  return (
    <section>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-balance text-2xl font-bold">AI access</h1>
          <p className="mt-1 max-w-3xl text-pretty text-sm text-muted-foreground">
            Connect Codex or Claude to customer knowledge through the permissioned DAS MCP service.
            Use employee OAuth or a scoped, expiring personal MCP key.
          </p>
        </div>
        <Badge variant="outline">{endpoint}</Badge>
      </div>
      <AccessBoundary />
      <ClientConnections endpoint={endpoint} />
      <PersonalMcpKeys endpoint={endpoint} />
      <OAuthScopes />
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <KnowledgeScope />
        <DeploymentBoundary />
      </div>
      <p className="mt-5 rounded-sm border border-dashed p-4 text-sm text-muted-foreground">
        A connection is bound to one selected customer. To work on another customer, authorize the
        connection again and select that customer. Existing grants stop working immediately if the
        employee or assignment is disabled.
      </p>
    </section>
  );
}
